import { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import adminservice from '../../../services/adminServices';
import Page from '../../../components/Page';
import Logo from '../../../assets/logo/logo.png';


const TABLE_ORDER = ["assessment_source", "assessment_modules", "questions"];

const TABLE_META = {
  assessment_source: {
    label: "Assessment Source",
    icon: "",
    description: "Root source records — no FK dependencies",
    fks: [],
  },
  assessment_modules: {
    label: "Assessment Modules",
    icon: "",
    description: "Linked to Assessment Source via assessment_source_id",
    fks: ["assessment_source_id → assessment_source.id"],
  },
  questions: {
    label: "Questions",
    icon: "",
    description: "Linked to Modules (chapter_id) and Source (assessment_source_id)",
    fks: [
      "chapter_id → assessment_modules.id",
      "assessment_source_id → assessment_source.id",
    ],
  },
};

const MAX_SELECT = 4;
const POLL_INTERVAL_MS = 3000;

function Badge({ color, children }) {
  const colors = {
    green: "bg-emerald-100 text-emerald-700 border-emerald-200",
    blue: "bg-blue-100 text-blue-700 border-blue-200",
    amber: "bg-amber-100 text-amber-700 border-amber-200",
    red: "bg-red-100 text-red-700 border-red-200",
    slate: "bg-slate-100 text-slate-600 border-slate-200",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border ${colors[color] || colors.slate}`}>
      {children}
    </span>
  );
}

function Spinner() {
  return (
    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
    </svg>
  );
}
function getStatusColor(status) {
  if (status === 'COMPLETED') return 'green';
  if (status === 'FAILED') return 'red';
  return 'blue';
}

function getSelectionColor(isAtMax, hasSelection) {
  if (isAtMax) return 'amber';
  if (hasSelection) return 'blue';
  return 'slate';
}

function getRowClassName(isBlocked, isSelected) {
  const base = 'flex items-center gap-3 px-4 py-3 transition-colors w-full text-left';
  if (isBlocked) return `${base} opacity-40 cursor-not-allowed bg-slate-50`;
  if (isSelected) return `${base} bg-indigo-50 hover:bg-indigo-100 cursor-pointer`;
  return `${base} hover:bg-slate-50 cursor-pointer`;
}

function getCheckboxClassName(isSelected, isBlocked) {
  const base = 'w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors';
  if (isSelected) return `${base} bg-indigo-600 border-indigo-600`;
  if (isBlocked) return `${base} border-slate-200 bg-white`;
  return `${base} border-slate-300 bg-white`;
}

function ProgressBar({ jobStatus }) {
  if (!jobStatus) return null;

  const { status, progress, results, errors } = jobStatus;

  const statusColors = {
    PENDING: "bg-amber-500",
    RUNNING: "bg-indigo-500",
    COMPLETED: "bg-emerald-500",
    FAILED: "bg-red-500",
  };

  return (
    <div className="mt-4 p-4 rounded-xl border border-slate-200 bg-white space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {(status === 'PENDING' || status === 'RUNNING') && <Spinner />}
          <span className="text-sm font-semibold text-slate-700">
            {{ PENDING: 'Queued…', RUNNING: 'Importing…', COMPLETED: 'Completed', FAILED: 'Failed' }[status] || status}
          </span>
        </div>
        <Badge color={getStatusColor(status)}>
          {progress?.percent ?? 0}%
        </Badge>
      </div>

      <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
        <div
          className={`h-2.5 rounded-full transition-all duration-500 ${statusColors[status] || 'bg-indigo-500'}`}
          style={{ width: `${progress?.percent ?? 0}%` }}
        />
      </div>

      <div className="flex gap-4 text-xs text-slate-500">
        <span>Processed: <strong className="text-slate-700">{progress?.processed ?? 0}</strong> / {progress?.total ?? 0}</span>
        <span>✅ Success: <strong className="text-emerald-600">{results?.successful ?? 0}</strong></span>
        <span>❌ Failed: <strong className="text-red-500">{results?.failed ?? 0}</strong></span>
      </div>

      {errors?.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-red-500 hover:text-red-700">
            View {errors.length} error(s)
          </summary>
          <div className="mt-2 bg-slate-800 text-red-300 rounded p-3 overflow-x-auto max-h-40 overflow-y-auto font-mono space-y-1">
            {errors.map((e, i) => (
              <div key={i}>{e.type === 'question' ? `Q#${e.questionId}` : `${e.type} #${e.id}`}: {e.error}</div>
            ))}
          </div>
        </details>
      )}

      {status === 'COMPLETED' && (
        <div className="text-xs text-emerald-600 font-medium">
          ✅ Import complete — {results?.successful} questions imported successfully.
        </div>
      )}
      {status === 'FAILED' && (
        <div className="text-xs text-red-600 font-medium">
          ❌ Import failed. Check errors above.
        </div>
      )}
    </div>
  );
}

function SourceSelector({ sources, selectedIds, onChange, disabled }) {
  const [search, setSearch] = useState('');

  const filtered = sources.filter(s =>
    s.title?.toLowerCase().includes(search.toLowerCase()) ||
    String(s.id).includes(search)
  );



  const toggle = (id) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter(x => x !== id));
    } else {
      if (selectedIds.length >= MAX_SELECT) return;
      onChange([...selectedIds, id]);
    }
  };

  const isAtMax = selectedIds.length >= MAX_SELECT;

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-700">Select Sources to Import</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Choose up to <strong>{MAX_SELECT}</strong> sources at a time
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge color={getSelectionColor(isAtMax, selectedIds.length > 0)}>
            {selectedIds.length} / {MAX_SELECT} selected
          </Badge>
          {selectedIds.length > 0 && (
            <button
              onClick={() => onChange([])}
              disabled={disabled}
              className="text-xs text-slate-400 hover:text-red-500 transition-colors"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      {isAtMax && (
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
          <span className="text-amber-600 text-xs font-medium">
            ⚠️ Maximum {MAX_SELECT} sources selected. Deselect one to choose another.
          </span>
        </div>
      )}

      <div className="px-4 py-2 border-b border-slate-100 bg-white">
        <input
          type="text"
          placeholder="Search by title or ID…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full text-xs border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-300"
        />
      </div>

      <div className="max-h-72 overflow-y-auto bg-white divide-y divide-slate-50">
        {filtered.length === 0 && (
          <p className="px-4 py-6 text-xs text-slate-400 text-center">No sources found</p>
        )}
        {filtered.map(source => {
          const isSelected = selectedIds.includes(source.id);
          const isBlocked = !isSelected && isAtMax;


          return (
            <button
              key={source.id}
              type="button"
              onClick={() => toggle(source.id)}
              disabled={disabled || isBlocked}
              className={getRowClassName(isBlocked, isSelected)}
            >
              <div className={getCheckboxClassName(isSelected, isBlocked)}>
                {isSelected && (
                  <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{source.title}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  {source?.question_count && <Badge color="slate">Question Count {source?.question_count ?? 0}</Badge>}
                  {source.type && <Badge color="slate">{source.type}</Badge>}
                  {source.complexity_level && <Badge color="blue">{source.complexity_level}</Badge>}
                </div>
              </div>

              {isSelected && (
                <span className="text-indigo-600 text-xs font-semibold flex-shrink-0">✓ Selected</span>
              )}
              {isBlocked && (
                <span className="text-slate-400 text-xs flex-shrink-0">Max reached</span>
              )}
            </button>
          );
        })}
      </div>


    </div>
  );
}

export default function ExportData() {
  const navigate = useNavigate();
  const [exportData, setExportData] = useState(null);
  const [loadingSources, setLoadingSources] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    loadExportData();
  }, []);

  const loadExportData = async () => {
    setLoadingSources(true);
    try {
      const res = await adminservice.getExportDataFromPublic();
      setExportData(res.data.result);
    } catch (err) {
      console.error('Failed to load sources:', err);
    } finally {
      setLoadingSources(false);
    }
  };

  useEffect(() => {
    if (!jobId) return;

    const poll = async () => {
      try {
        const res = await adminservice.getImportJobStatus(jobId);
        setJobStatus(res.data);
        if (res.data.status === 'COMPLETED' || res.data.status === 'FAILED') {
          setExporting(false);
          clearInterval(intervalId);
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    };

    poll();
    const intervalId = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [jobId]);

  const handleImport = async () => {
    if (!selectedIds.length) return;
    if (selectedIds.length > MAX_SELECT) return;

    setExporting(true);
    setJobStatus(null);
    setJobId(null);

    try {
      const res = await adminservice.ImportDataFromPublic({ sourceIds: selectedIds });
      const { jobId: newJobId } = res.data;
      setExporting(false);
      setTimeout(() => {
        navigate('/app/sources')
      }, 2000);

      if (!newJobId) throw new Error('No jobId returned from server');
      setJobId(newJobId);
    } catch (err) {
      console.error('Import failed to start:', err);
      setExporting(false);
      setJobStatus({
        status: 'FAILED',
        progress: { percent: 0, processed: 0, total: 0 },
        results: { successful: 0, failed: 0 },
        errors: [{ error: err.message }]
      });
    }
  };

  const sources = exportData?.assessment_sources || [];
  // const isRunning = exporting || jobStatus?.status === 'PENDING' || jobStatus?.status === 'RUNNING';
  const isRunning = exporting;
  const canImport = selectedIds.length > 0 && selectedIds.length <= MAX_SELECT && !isRunning;

  const selectedSources = sources.filter((s) => selectedIds.includes(s.id));
  function PreviewModal({ open, onClose, selectedSources, onConfirmImport, importing }) {
    if (!open) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-bold text-slate-800">Preview Selected Sources</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                {selectedSources.length} source{selectedSources.length > 1 ? 's' : ''} will be imported
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 text-lg leading-none"
              aria-label="Close preview"
            >
              ✕
            </button>
          </div>

          <div className="overflow-y-auto divide-y divide-slate-50">
            {selectedSources.map((source) => (
              <div key={source.id} className="flex items-center gap-3 px-5 py-3">
                <div className="w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 bg-indigo-600 border-indigo-600">
                  <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{source.title}</p>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    {source?.question_count != null && (
                      <Badge color="slate">Question Count {source.question_count}</Badge>
                    )}
                    {source.type && <Badge color="slate">{source.type}</Badge>}
                    {source.complexity_level && <Badge color="blue">{source.complexity_level}</Badge>}
                  </div>
                </div>

                <span className="text-indigo-600 text-xs font-semibold flex-shrink-0">✓ Selected</span>
              </div>
            ))}

            {selectedSources.length === 0 && (
              <p className="px-5 py-6 text-xs text-slate-400 text-center">No sources selected</p>
            )}
          </div>

          <div className="px-5 py-4 border-t border-slate-200 flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirmImport}
              disabled={importing || selectedSources.length === 0}
              className="flex items-center gap-2 px-5 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {importing ? <><Spinner /> Importing…</> : 'Confirm & Import'}
            </button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <Page title=''>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50">
        <header className="bg-white border-b border-slate-200 px-6 py-4 shadow-sm">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center text-white font-bold text-sm"> */}
              <img id='logoKSS' src={Logo} alt="Logo" className="h-10 sm:h-12 w-auto object-contain" />
              {/* </div> */}
              <div>
                <h1 className="text-base font-bold text-slate-800">Schema Export Tool</h1>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-2">
            <p className="text-sm font-semibold text-blue-800">📋 How to import</p>
            <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
              <li>Sources, their modules, and all questions will be copied to your tenant schema with remapped IDs.</li>
              <li>Select up to <strong>{MAX_SELECT} sources</strong> at a time from the list below.</li>
              <li>Click <strong>Import Selected</strong> — the job runs in the background.</li>
              <li>Progress updates every 3 seconds. You can run multiple batches after each completes.</li>
            </ol>
          </div>



          {loadingSources ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center">
              <Spinner /> Loading sources…
            </div>
          ) : (
            <SourceSelector
              sources={sources}
              selectedIds={selectedIds}
              onChange={setSelectedIds}
              disabled={isRunning}
            />
          )}

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowPreview(true)}
                disabled={selectedIds.length === 0 || isRunning}
                className="flex items-center gap-2 px-6 py-2 rounded-lg border border-indigo-600 text-indigo-600 text-sm font-semibold hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Preview
              </button>
              <button
                onClick={handleImport}
                disabled={!canImport}
                className="flex items-center gap-2 px-6 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                {isRunning ? <><Spinner /> Importing…</> : `Import ${selectedIds.length > 0 ? `${selectedIds.length} Source${selectedIds.length > 1 ? 's' : ''}` : 'Selected'}`}
              </button>

              {!canImport && !isRunning && selectedIds.length === 0 && (
                <p className="text-xs text-slate-400">Select at least 1 source to import</p>
              )}

              {/* {jobId && (
              <span className="text-xs text-slate-400 font-mono">
                Job: <span className="text-slate-600">{jobId}</span>
              </span>
            )} */}
            </div>

            <ProgressBar jobStatus={jobStatus} />
            <PreviewModal
              open={showPreview}
              onClose={() => setShowPreview(false)}
              selectedSources={selectedSources}
              importing={isRunning}
              onConfirmImport={async () => {
                setShowPreview(false);
                await handleImport();
              }}
            />
          </div>

        </main>
      </div>
    </Page>
  );
}